import { gql, useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
} from "@mui/material";
import { isEqual } from "lodash";
import { useState } from "react";

import TransferList, { TransferListProps } from "~/components/common/forms/TransferList"
import Help from "~/components/common/Help";
import { GlobalUserRole } from "@caredna-marlin/utils/userRoles";
import { PROVIDER_ROLES_QUERY } from "./ProviderAvailabiltyMenu";
import { ProviderRolesQuery } from "~/graphql/__generated__/graphql";

const ShiftManagement = ({ data, onClose, providers }: { onClose: () => void, data: ProviderRolesQuery, providers: TransferListProps["selected"] }) => {
  const [addProvidersRole, { loading: loadingAdd }] = useMutation(ADD_PROVIDER_ROLE, { refetchQueries: [PROVIDER_ROLES_QUERY]});
  const [removeProvidersRole, { loading: loadingRemove }] = useMutation(REMOVE_PROVIDER_ROLE, { refetchQueries: [PROVIDER_ROLES_QUERY]});

  const [localProviders, setLocalProviders] = useState<TransferListProps["selected"]>(providers);

  const noLocalChanges = isEqual(localProviders, providers);

  const onSave = async () => {
    if (!data) { return; }

    const ouids = data.provider
      .map(p => p.OrganizationUser?.id)
      .filter(id => id !== undefined);

    const addRoleIds = ouids.filter(id => localProviders[id].selected && !providers[id].selected)
    if (addRoleIds.length) {
      addProvidersRole({ variables: {
        organizationUserIds: addRoleIds,
        role: GlobalUserRole.selfScheduleProvider,
      }});
    }

    const removeRoleIds = ouids.filter(id => !localProviders[id].selected && providers[id].selected)
    if (removeRoleIds.length) {
      removeProvidersRole({ variables: {
        organizationUserIds: removeRoleIds,
        role: GlobalUserRole.selfScheduleProvider
      }});
    }
  }

  return <Card>
    <CardHeader
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        flex: 1,
        flexDirection: "row",
      }}
      action={<IconButton aria-label="close" onClick={onClose}>
        <CloseIcon />
      </IconButton>}
      title={<Typography variant="h6">Provider Availability</Typography>}
    />
    <CardContent>
      <TransferList
        unselectedLabel={<>
          Only in web app
          <Help text="Providers in this list cannot manage their own availability in the mobile app; it must be done by a manager or admin through this web interface." />
        </>}
        selectedLabel={<>
          In mobile app
          <Help text="Providers in this list can manage their own availability in the moblie app." />
        </>}
        selected={localProviders}
        setSelected={setLocalProviders}
      />
    </CardContent>
    <CardActions>
      <Button variant="contained" size="small" color="primary" disabled={noLocalChanges} onClick={onSave} loading={loadingAdd || loadingRemove}>
        Save
      </Button>
      <Button variant="outlined" size="small" color="secondary" disabled={loadingAdd || loadingRemove} onClick={() => setLocalProviders(providers)}>
        Reset
      </Button>
    </CardActions>
  </Card>
}

export default ShiftManagement;

const ADD_PROVIDER_ROLE = gql(`
  mutation AddProvidersRole(
    $organizationUserIds: [Int!]!
    $role: OrganizationUserRoleGlobalUserRoleEnum!
  )
  @permission(requires: manageProviderShifts)
  {
    addRoleToOrganizationUsers(
      ids: $organizationUserIds
      role: $role
    ) {
      count
    }
  }
`);

const REMOVE_PROVIDER_ROLE = gql(`
  mutation RemoveProvidersRole(
    $organizationUserIds: [Int!]!
    $role: OrganizationUserRoleGlobalUserRoleEnum!
  )
  @permission(requires: manageProviderShifts)
  {
    removeRoleFromOrganizationUsers(
      ids: $organizationUserIds
      role: $role
    ) {
      count
    }
  }
`);
